import Tooltip from '@atlaskit/tooltip';
import {
  CopyableText,
  FormattedCurrency,
  FormattedDate,
  getEntitlementRouteFromLineItem,
  HumanReadableIdentifierMissingIcon,
  isAccess,
  isResourceAccess,
} from '@css-xp/base';
import {
  AppliedSLACredit,
  BillingDocWithLineItems,
  Invoice,
  InvoiceLineItem,
  InvoiceLineItemDiscount,
  InvoiceWithLineItems,
  PaymentMethods,
} from '@atlassian/cssxp-types-billing';
import { SourceSystem, Permissions, Resources } from '@atlassian/cssxp-typescommon';
import * as InvoiceUtils from '@atlassian/cssxp-utils-invoice';
import isEmpty from 'lodash/isEmpty';
import sumBy from 'lodash/sumBy';
import toNumber from 'lodash/toNumber';
//import { getHumanReadableNameFromPaymentMethod } from '@atlassian/cssxp-utils-invoice';
import isNil from 'lodash/isNil';
import toString from 'lodash/toString';
import * as React from 'react';
import { Link } from 'react-router-dom';
import RefundResults from './invoice-detail-refund-results';
import { formatInvoiceLineItems, formatInvoiceLineItemsCCP } from '../invoice-page-utils';
import { getReadablePromotionValue } from '../../../../../support/entitlements/statuspage/src/tabs/utils/page-tabs-elements';
import { Promotion } from '@css-xp/graphtypes';
import { isPaymentTerm } from '@atlassian/cssxp-utils-invoice';

const getIdentifier = (item: InvoiceLineItem, sourceSystem?: SourceSystem | null) => {
  if (sourceSystem === SourceSystem.CCP) {
    if (item.entitlementNumber) {
      return <>{item.entitlementNumber}</>;
    }
    return (
      <>
        {item.id} <HumanReadableIdentifierMissingIcon />
      </>
    );
  }
  return <>SEN-{item.sen || item.id}</>;
};

const GetEntitlementUUID = (item: InvoiceLineItem, sourceSystem?: SourceSystem | null) => {
  const hasFinanceUserPermission =
    isAccess(Permissions.SUBSCRIPTION_ENTITLEMENT_UUID_READ) && isResourceAccess(Resources.INVOICE_ALL);
  if (hasFinanceUserPermission && sourceSystem === SourceSystem.CCP) {
    return <CopyableText text={item.id} />;
  }
  return;
};
const getCloudSiteId = (item: InvoiceLineItem) => {
  return <> {item?.cloudSiteId} </>;
};
const InvoiceLineItemDescription = ({
  item,
  sourceSystem,
}: {
  item: InvoiceLineItem;
  sourceSystem?: SourceSystem | null;
}) => {
  const entitlementRoute = getEntitlementRouteFromLineItem(item);
  return (
    <>
      <span>
        <Link to={entitlementRoute}>
          <strong>{getIdentifier(item, sourceSystem)}</strong>
        </Link>

        <>
          {sourceSystem !== SourceSystem.CCP && item.cloudSiteId ? (
            <>
              &nbsp; | &nbsp;
              <Link to={entitlementRoute}>
                <strong>{getCloudSiteId(item)}</strong>
              </Link>
            </>
          ) : undefined}
        </>
      </span>
      <div
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          maxWidth: '400px',
        }}
      >
        {GetEntitlementUUID(item, sourceSystem)}
      </div>
      <div
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          maxWidth: '400px',
        }}
        data-testid="line-item-description"
      >
        {item.description}
        {item.unitCount && item.unitCount > 0 && `${' - '}${item.unitCount} units`}
        {item.platform?.toLowerCase() === 'cloud' && item.cloudSiteHostname && (
          <>
            <br />
            <Tooltip content={item.cloudSiteHostname} tag="span" truncate>
              <Link to={entitlementRoute}>{item.cloudSiteHostname}</Link>
            </Tooltip>
          </>
        )}
      </div>
    </>
  );
};

const PaymentDetails = ({ paymentMethod, creditCard, paypalAccountEmail, invoiceDueDays }: InvoiceWithLineItems) => {
  if (!paymentMethod) {
    return null;
  }

  if (InvoiceUtils.isPaidByCreditCard(paymentMethod)) {
    return (
      <table className="receipt-table">
        <tbody>
          <tr>
            <td>Payment method:</td>
            <td>Credit card</td>
          </tr>
          <tr>
            <td>Card number:</td>
            <td>
              {`**** **** **** ${creditCard.last4Numbers ?? ''}`}
              {creditCard.brand ? `(${creditCard.brand})` : null}
            </td>
          </tr>
          <tr>
            <td>Card holder:</td>
            <td>{creditCard.cardHolderName}</td>
          </tr>
          <tr>
            <td>Card expiry:</td>
            {creditCard.expirationMonth && creditCard.expirationYear ? (
              <td>{`${creditCard.expirationMonth}/${creditCard.expirationYear}`}</td>
            ) : (
              <td>Not Available</td>
            )}
          </tr>
        </tbody>
      </table>
    );
  }

  if (isPaymentTerm(paymentMethod)) {
    return (
      <table className="receipt-table">
        <tbody>
          <tr>
            <td>Payment method:</td>
            <td>{InvoiceUtils.getHumanReadableNameFromPaymentMethod(paymentMethod)}</td>
          </tr>
          {paymentMethod === 'DEFERRED' && (
            <tr>
              <td>Number of days:</td>
              <td>{invoiceDueDays}</td>
            </tr>
          )}
        </tbody>
      </table>
    );
  }

  if (paymentMethod === 'PAIDPAYPAL') {
    return (
      <table className="receipt-table">
        <tbody>
          <tr>
            <td>Payment method:</td>
            <td>PayPal</td>
          </tr>
          <tr>
            <td>PayPal account:</td>
            <td>{paypalAccountEmail}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  return null;
};

/**
 * Component is type casted to be used in BillingDoc Component having details of the invoice
 * @param billingDoc
 * @constructor
 */
const InvoiceLineItems = (billingDoc: BillingDocWithLineItems) => {
  const invoice = billingDoc as InvoiceWithLineItems;
  return <StatelessInvoiceLineItems invoice={invoice} />;
};

const getPaidLabel = (billingDoc: Invoice) => {
  if (
    billingDoc.paymentMethod &&
    // leaving as array in case more items needed to be added
    (['DEFERRED'] as PaymentMethods[]).includes(billingDoc.paymentMethod)
  ) {
    if (billingDoc.status && ['PAID'].includes(billingDoc.status)) {
      return 'paid';
    }
    return 'due';
  }
  return 'paid';
};

const getPromotionReasonWithDescription = (promotion: Promotion, discount: InvoiceLineItemDiscount | null = null) => {
  return (
    <div>
      {promotion?.title && (
        <div>
          {promotion?.title} (
          {discount?.percent ? discount?.percent + '% discount' : getReadablePromotionValue(promotion as Promotion)})
        </div>
      )}
    </div>
  );
};

const showPromotionAdjustments = (lineItem: InvoiceLineItem) => {
  return lineItem?.promotionItems ? (
    <div data-testid="invoice-promotions">
      <h3>Adjustment(s)</h3>
      {lineItem?.promotionItems.map((promotion, index) => (
        <ul key={index}>
          <li>{getPromotionReasonWithDescription(promotion as Promotion, lineItem?.discounts?.[0] ?? null)}</li>
        </ul>
      ))}
    </div>
  ) : null;
};

const adjustedAmount = (subtotal: string | null | undefined, discount: InvoiceLineItemDiscount[] | null): number => {
  const actualSubtotal = toNumber(subtotal);
  const actualDiscount = sumBy(discount, 'amount');
  if (!isNaN(actualSubtotal) && actualSubtotal !== 0 && !isNaN(actualDiscount)) {
    return actualSubtotal - actualDiscount;
  }

  return 0;
};

const getColGroups = (sourceSystem: SourceSystem | null | undefined, canShowPromotionOnInvoice: boolean) => {
  if (canShowPromotionOnInvoice && sourceSystem === SourceSystem.CCP) {
    return (
      <colgroup>
        <col className="quantity-column" />
        <col className="description-column" />
        <col className="billing-period-column" />
        <col className="unit-amount-column" />
        <col className="adjustment-amount-column" />
        <col className="amount-column" />
      </colgroup>
    );
  } else {
    return (
      <colgroup>
        <col className="description-column" />
        <col className="billing-period-column" />
        <col className="amount-column" />
      </colgroup>
    );
  }
};

const getTableHeader = (sourceSystem: SourceSystem | null | undefined, canShowPromotionOnInvoice: boolean) => {
  if (canShowPromotionOnInvoice && sourceSystem === SourceSystem.CCP) {
    return (
      <tr>
        <th>Tier/Qty</th>
        <th>Details</th>
        <th>Billing period</th>
        <th className="amount-cell">Unit Price</th>
        <th className="amount-cell">Adjustment(s)</th>
        <th className="amount-cell">Amount</th>
      </tr>
    );
  } else {
    return (
      <tr>
        <th>Details</th>
        <th>Billing period</th>
        <th className="amount-cell">Amount</th>
      </tr>
    );
  }
};

const getPromotionReasonCode = (promotion: Promotion | undefined, discount: InvoiceLineItemDiscount) => {
  const promoReasonFilter = promotion?.applicationReason?.customisable?.id?.anyOf?.filter(
    (applicationReason) => applicationReason?.id === discount?.reasonCode
  ) ?? [
    {
      id: promotion?.applicationReason?.id,
      readableName: promotion?.applicationReason?.readableName,
    },
  ];
  const promotionInvoiceNote =
    (promoReasonFilter?.length ? promoReasonFilter?.[0]?.readableName : promotion?.purpose?.invoiceNote) || '';
  return promotionInvoiceNote;
};

/**
 * StatelessInvoiceLineItems component to show invoice line items in side pane as well as full page view
 * In sidebar pane, we don't have to show child products in case of HAMS
 * In full view page, we show products as well
 */

export const StatelessInvoiceLineItems = ({ invoice }: { invoice: InvoiceWithLineItems }): JSX.Element => {
  const canShowPromotionOnInvoice = true; //useSelector(getFeatureFlag(PROMOTIONS_ON_INVOICE));
  const getLineItemKey = (item: InvoiceLineItem) => {
    // All these fields are potentially null so use the
    // combination of them to create a unique key for React
    const identifier = invoice.sourceSystem === SourceSystem.CCP ? item.entitlementNumber || item.id : item.sen;
    const description = item.description ?? '';
    const amount = item.amount ?? '';

    return `${identifier}-${description}-${amount}`;
  };
  const isCCPInvoice = invoice.sourceSystem === SourceSystem.CCP;
  const lineItems: InvoiceLineItem[] = isCCPInvoice
    ? formatInvoiceLineItemsCCP(invoice.lineItems)
    : formatInvoiceLineItems(invoice.lineItems);
  const hasUpgradeCredits = toNumber(invoice.upgradeCredits) < 0;
  const discountAdjustedSubtotal = (toNumber(invoice.subtotal) - toNumber(invoice.totalDiscount ?? 0.0)).toFixed(2);
  return (
    <>
      <table className="aui billing-line-items" data-testid="invoice-line-items-table">
        {getColGroups(invoice?.sourceSystem, canShowPromotionOnInvoice)}
        <thead>{getTableHeader(invoice?.sourceSystem, canShowPromotionOnInvoice)}</thead>
        <tbody>
          {lineItems.map((item) => (
            <React.Fragment key={getLineItemKey(item)}>
              <tr className="line-item parent-item">
                <td>
                  <InvoiceLineItemDescription item={item} sourceSystem={invoice.sourceSystem} />
                  {canShowPromotionOnInvoice && showPromotionAdjustments(item)}
                </td>
                <td>
                  <FormattedDate date={item.startDate} /> - <FormattedDate date={item.endDate} />
                </td>
                <td className="amount-cell">
                  <FormattedCurrency value={item.subtotal ? item.subtotal : 0} currency={invoice.paymentCurrency} />
                </td>
                {canShowPromotionOnInvoice && invoice.sourceSystem === SourceSystem.CCP && (
                  <>
                    <td className="amount-cell">
                      {item?.discounts?.map((discount: InvoiceLineItemDiscount, index: number) => (
                        <>
                          {getPromotionReasonCode(item?.promotionItems?.[index] as Promotion, discount)}
                          <div>
                            <FormattedCurrency
                              key={`${discount.amount}-${index}`}
                              value={discount.amount ? discount.amount : 0}
                              currency={invoice.paymentCurrency}
                            />
                          </div>
                        </>
                      ))}
                    </td>
                    <td className="amount-cell">
                      <FormattedCurrency
                        value={adjustedAmount(item.subtotal, item.discounts)}
                        currency={invoice.paymentCurrency}
                      />
                    </td>
                  </>
                )}
              </tr>
              {(item.products ?? []).map((product) => (
                <tr key={getLineItemKey(product)} className="line-item child-item">
                  <td>
                    {invoice.sourceSystem === SourceSystem.CCP && (
                      <div>{getIdentifier(product, invoice.sourceSystem)}</div>
                    )}
                    {invoice.sourceSystem === SourceSystem.HAMS && (
                      <Link to={getEntitlementRouteFromLineItem(item)}>
                        <strong>{product?.entitlement?.entitlementNumber}</strong>
                      </Link>
                    )}
                    <div>{product.description}</div>
                  </td>
                  <td>
                    <FormattedDate date={product.startDate} /> - <FormattedDate date={product.endDate} />
                  </td>
                  <td className="amount-cell">
                    <FormattedCurrency
                      value={product.subtotal ? product.subtotal : 0}
                      currency={invoice.paymentCurrency}
                    />
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <table className="billing-total-table" data-testid="invoice-total-table">
        <tbody>
          <tr>
            <td>Subtotal</td>
            <td>
              <FormattedCurrency
                value={canShowPromotionOnInvoice ? discountAdjustedSubtotal : invoice.subtotal ?? 0}
                currency={invoice.paymentCurrency}
              />
            </td>
          </tr>
          {hasUpgradeCredits && (
            <tr>
              <td>Upgrade credit</td>
              <td>
                <FormattedCurrency
                  value={Math.abs(toNumber(invoice.upgradeCredits)) ?? 0}
                  currency={invoice.paymentCurrency}
                />
              </td>
            </tr>
          )}
          <tr>
            <td>Total excl. tax</td>
            <td data-testid="total-excl-tax">
              <FormattedCurrency
                value={
                  toString(
                    toNumber(canShowPromotionOnInvoice ? discountAdjustedSubtotal : invoice.subtotal ?? 0) -
                      Math.abs(toNumber(invoice.upgradeCredits ?? 0))
                  ) ?? 0
                }
                currency={invoice.paymentCurrency}
              />
            </td>
          </tr>
          <tr>
            <td>Tax</td>
            <td data-testid="tax-cell">
              <FormattedCurrency value={invoice.taxes ?? 0} currency={invoice.paymentCurrency} />
            </td>
          </tr>
          {invoice.slaCredits.map((credit: AppliedSLACredit) => (
            <React.Fragment key={credit.creditId}>
              <tr data-testid="applied-credit-amount">
                <td>SLA credit - {credit.notes}</td>
                <td className="amount-cell">
                  (
                  <FormattedCurrency value={credit.amountExTax} currency={credit.currency} />)
                </td>
              </tr>
              <tr data-testid="applied-credit-tax">
                <td>Tax</td>
                <td>
                  (
                  <FormattedCurrency value={credit.taxAmount} currency={credit.currency} />)
                </td>
              </tr>
            </React.Fragment>
          ))}
          <tr className="billing-grand-total">
            <td data-testid="total-paid-label">Total amount {getPaidLabel(invoice)}</td>
            <td data-testid="billing-grand-total">
              <FormattedCurrency value={invoice.total ?? 0} currency={invoice.paymentCurrency} showCurrencyCode />
            </td>
          </tr>
        </tbody>
      </table>
      <div style={{ marginBottom: 32 }} data-testid="payment-details">
        <PaymentDetails {...invoice} />
      </div>
      {!isNil(invoice?.memo) && (
        <div style={{ marginBottom: 32 }}>
          <h2>Memo</h2>
          <p style={{ marginTop: 8 }}>{invoice?.memo}</p>
        </div>
      )}
      {!isEmpty(invoice.refunds) && (
        <div style={{ marginBottom: 32 }}>
          <RefundResults refunds={invoice.refunds} source={invoice.sourceSystem} />
        </div>
      )}
    </>
  );
};

export default InvoiceLineItems;
